import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { Banner, BlockStack, Button, Card, Frame, Page, Text } from '@shopify/polaris';
import i18next from 'i18next';

/**
 * The last line before a white screen.
 *
 * ── Why a class component in a React 19 codebase ───────────────────────────
 *
 * There is still no hook for this. `componentDidCatch` / `getDerivedStateFrom-
 * Error` are the only way to catch a render-phase throw, and every "hooks
 * version" of an error boundary is a class with a function wrapped around it.
 *
 * ── What it does NOT catch, which is the important half ────────────────────
 *
 * Nothing asynchronous. An error boundary sees render, lifecycle and
 * constructor throws — not a rejected promise in an event handler, not a
 * `setTimeout`, not a failed `fetch`. Those must be caught where they happen and
 * surfaced as a Banner; the save path and the billing calls both do that. If a
 * bug ever seems to "escape" this boundary, that is almost certainly why.
 *
 * ── Why a reload button and not a retry ────────────────────────────────────
 *
 * Re-rendering the same tree that just threw usually throws again, and a retry
 * that visibly does nothing is worse than no retry. The state that produced the
 * error is upstream — a half-decoded config, a bad route param — so the honest
 * recovery is a fresh boot. The merchant's saved config is untouched either way:
 * nothing is written without an explicit save.
 */

/**
 * Translate, with an English string baked in as the fallback.
 *
 * ⚠ This component renders when something has ALREADY gone wrong — and i18next
 * itself is one of the things that can have gone wrong. A plain `t()` here would
 * show raw message keys on the very screen whose only job is to be readable.
 * `useTranslation` is not an option either: this has to be a class, because
 * `getDerivedStateFromError` has no hook equivalent.
 */
function tr(key: string, english: string): string {
  try {
    if (!i18next.isInitialized) return english;
    const out = i18next.t(key);
    return out === key ? english : out;
  } catch {
    return english;
  }
}

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Structured, and to `console.error` rather than a network beacon: this app
    // holds zero access scopes and sends nothing about a merchant anywhere. If
    // this ever becomes a report, it goes through the same consent the Web
    // Vitals beacon does — and the privacy policy changes in the same commit.
    console.error(
      JSON.stringify({
        evt: 'admin.render.error',
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 4).join(' | '),
        componentStack: info.componentStack?.split('\n').slice(0, 4).join(' | '),
      })
    );
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Frame>
        <Page title="Confetto">
          <Card>
            <BlockStack gap="400">
              {/* ⚠ Banner, never a toast — BFS §4.2.4. This is the one error a
                  merchant cannot possibly act on if it vanishes on a timer. */}
              <Banner
                tone="critical"
                title={tr('app.brokeTitle', 'Something in Confetto stopped working')}
              >
                <p>
                  {tr(
                    'app.safeNote',
                    'Your settings are safe — nothing is saved to your store unless you press Save, and this happened before any save.'
                  )}
                </p>
              </Banner>
              <Text as="p">{tr('app.reloadClears', 'Reloading usually clears it.')}</Text>
              <div>
                <Button variant="primary" onClick={() => window.location.reload()}>
                  {tr('app.reload', 'Reload Confetto')}
                </Button>
              </div>
              <Text as="p" tone="subdued" variant="bodySm">
                {tr('app.sendSupport', 'If it keeps happening, send this to support@afixer.app:')}{' '}
                {error.message}
              </Text>
            </BlockStack>
          </Card>
        </Page>
      </Frame>
    );
  }
}
